import { useTradedoublerIntegrationOrderGtmEvents } from '@hooks/use-order-gtm-events'
import { ReservationDetails } from '@modules/reservation/models'
import { ProductKinds } from '@components/payments/payment-selection-view'
import { SubscriptionDetails } from '@modules/subscription/models'
import { SubscriptionContractDetails } from '@modules/subscription-contract/models'
import { usePaymentSuccessProductDetails } from '@hooks/gtm/use-payment-success-product-details'
import { Seller } from '@models/base'
import { asDecimal } from '@helpers/utils'
import { normalizePaymentSuccessKind } from '@hooks/gtm/payment-success-kind-aliases'

const TRADEDOUBLER_COOKIE_NAME = 'tduid='
const DEFAULT_CURRENCY = 'PLN'

interface CodeUpgradeDetails {
  email: string
  price_brutto: string
  token: string
}

interface ProductPackageDetails {
  number: string
  price_brutto: string
  required_payment: string
  created: string
  client: { email: string }
  seller: Seller | null
}

// Tradedeoubler integration orders data

export const usePaymentSuccessOrderTradedeoublerEvent = (kind: string | undefined, token: string | undefined): void => {
  const { sendOrderData } = useTradedoublerIntegrationOrderGtmEvents()
  const normalizedKind = normalizePaymentSuccessKind(kind)

  const sendReservationOrderData = (reservationDetails: ReservationDetails) => {
    const orderValue =
      reservationDetails.prices.order_value_tradedoubler || reservationDetails.prices.residence.total_price_brutto

    // eslint-disable-next-line no-console
    console.log('[Tradedoubler] orderValue check', {
      kind: normalizedKind,
      orderValue,
      reservationNumber: reservationDetails.reservation_number,
      token: reservationDetails.token,
    })

    // Temporary diagnostic bypass for testing.
    // Restore the `orderValue <= 0` guard after TradeDoubler verification is complete.
    if (asDecimal(orderValue).lte(0)) {
      // eslint-disable-next-line no-console
      console.log('[Tradedoubler] orderValue <= 0 bypassed for testing', {
        kind: normalizedKind,
        orderValue,
        reservationNumber: reservationDetails.reservation_number,
        token: reservationDetails.token,
      })
    }

    sendOrderData({
      orderNumber: reservationDetails.reservation_number || reservationDetails.token,
      orderValue,
      email: reservationDetails.email,
      currency: DEFAULT_CURRENCY,
      voucher: reservationDetails.promocode?.code ?? null,
      eventTime: reservationDetails.created_at,
    })
  }

  const sendSubscriptionWithBenefitsOrderData = (subscription: SubscriptionDetails) => {
    sendOrderData({
      orderNumber: subscription.number,
      orderValue: subscription.product.price_brutto,
      email: subscription.client.email,
      currency: DEFAULT_CURRENCY,
    })
  }

  const sendGoHolidayOrderData = (subscription: SubscriptionDetails) => {
    sendOrderData({
      orderNumber: subscription.number,
      orderValue: subscription.product.price_brutto,
      email: subscription.client.email,
      currency: DEFAULT_CURRENCY,
    })
  }

  const sendSubscriptionContractOrderData = (subscription: SubscriptionContractDetails) => {
    sendOrderData({
      orderNumber: subscription.token,
      orderValue: subscription.single_price_brutto,
      email: subscription.client.email,
      currency: DEFAULT_CURRENCY,
    })
  }

  const sendCodesConversionOrderData = (code: CodeUpgradeDetails) => {
    sendOrderData({
      orderNumber: code.token,
      orderValue: code.price_brutto,
      email: code.email,
      currency: DEFAULT_CURRENCY,
    })
  }

  const sendProductPackagesOrderData = (productPackage: ProductPackageDetails) => {
    const orderValue = productPackage.price_brutto || productPackage.required_payment

    // eslint-disable-next-line no-console
    console.log('[Tradedoubler] orderValue check', {
      kind: normalizedKind,
      orderValue,
      number: productPackage.number,
    })

    // Temporary diagnostic bypass for testing.
    // Restore the `orderValue <= 0` guard after TradeDoubler verification is complete.
    if (asDecimal(orderValue).lte(0)) {
      // eslint-disable-next-line no-console
      console.log('[Tradedoubler] orderValue <= 0 bypassed for testing', {
        kind: normalizedKind,
        orderValue,
        number: productPackage.number,
      })
    }

    sendOrderData({
      orderNumber: productPackage.number,
      orderValue,
      email: productPackage.client.email,
      currency: DEFAULT_CURRENCY,
      eventTime: productPackage.created,
    })
  }

  const sendPaymentSuccessEvent = (
    productDetails:
      | ReservationDetails
      | SubscriptionDetails
      | SubscriptionContractDetails
      | CodeUpgradeDetails
      | ProductPackageDetails,
  ) => {
    // Zgoda na Tradedoublery jest akceptowana automatycznie przez `tdconv('cookie-consent', ['COOKIE_CONSENT'])`,
    // więc wykrycie pliku cookie ma znaczenie tylko diagnostyczne.
    const hasTradedoublerCookie = document.cookie.includes(TRADEDOUBLER_COOKIE_NAME)

    const isEcommercePurchase = (productDetails as { seller: Seller | null }).seller?.department === 'ecommerce'
    const isClientStandalonePurchase = (productDetails as { seller: Seller | null }).seller === null

    // eslint-disable-next-line no-console
    console.log('[Tradedoubler] evaluating context', {
      kind,
      hasCookie: hasTradedoublerCookie,
      isEcommercePurchase,
      isClientStandalonePurchase,
    })

    let isSupportedKind = false

    if (normalizedKind === ProductKinds.RESERVATION_DETAILS) {
      isSupportedKind = true
      sendReservationOrderData(productDetails as ReservationDetails)
    }

    if (normalizedKind === ProductKinds.SUBSCRIPTIONS_WITH_BENEFITS) {
      isSupportedKind = true
      sendSubscriptionWithBenefitsOrderData(productDetails as SubscriptionDetails)
    }

    if (normalizedKind === ProductKinds.GO_HOLIDAY) {
      isSupportedKind = true
      sendGoHolidayOrderData(productDetails as SubscriptionDetails)
    }

    if (normalizedKind === ProductKinds.SUBSCRIPTION_CONTRACT) {
      isSupportedKind = true
      sendSubscriptionContractOrderData(productDetails as SubscriptionContractDetails)
    }

    if (normalizedKind === ProductKinds.CODES_CONVERSION) {
      isSupportedKind = true
      sendCodesConversionOrderData(productDetails as CodeUpgradeDetails)
    }

    if (normalizedKind === ProductKinds.ENDLESS_HOLIDAY) {
      isSupportedKind = true
      sendProductPackagesOrderData(productDetails as ProductPackageDetails)
    }

    if (!isSupportedKind) {
      // eslint-disable-next-line no-console
      console.log('[Tradedoubler] payment_success blocked', {
        reason: 'unsupported normalizedKind',
        kind,
        normalizedKind,
        token,
      })
    }
  }

  usePaymentSuccessProductDetails(kind, token, sendPaymentSuccessEvent)
}
